import React from 'react';
import { Form, Button } from 'react-bootstrap';
import { FormModeToggle, FormAlerts, FormSubmitRow, DropdownField, RepeatableFormSection } from '../../common';
import { useSuppliersForm } from './useSuppliersForm';
import { useAppStore } from '../../../store/appStore';

const SuppliersForm = () => {
  const insertBasicData = useAppStore((s) => s.insertBasicData);
  const isAnySectionLoading = Object.values(insertBasicData.sectionLoading || {}).some(Boolean);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    suppliersLoading,
    filteredSuppliers,
    supplierDropdownRef,
    handleModeChange,
    handleChange,
    handleSupplierSearchChange,
    handleSelectSupplier,
    handleSubmit,
    handleDelete,
    isEditMode,
    setSupplierFieldTouched,
    setShowSupplierDropdown,
    contacts,
    contactsLoading,
    addContact,
    updateContact,
    removeContact
  } = useSuppliersForm();

  return (
    <>
      <FormModeToggle mode={mode} onModeChange={handleModeChange} />
      <FormAlerts error={formError} success={formSuccess} />

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Supplier Name <span className="text-danger">*</span></Form.Label>
          {isEditMode ? (
            <DropdownField
              ref={supplierDropdownRef}
              value={supplierSearchQuery}
              onChange={handleSupplierSearchChange}
              placeholder="Search by supplier name..."
              disabled={suppliersLoading}
              loading={suppliersLoading}
              loadingMessage="Loading suppliers..."
              showDropdown={showSupplierDropdown && !suppliersLoading}
              options={filteredSuppliers}
              emptyMessage={supplierSearchQuery.trim() ? 'No matching suppliers' : 'Type to search suppliers'}
              getLabel={(s) => s.name || '(Unnamed)'}
              getItemKey={(s) => s.id}
              onSelect={handleSelectSupplier}
              onFocus={() => {
                setSupplierFieldTouched(true);
                setShowSupplierDropdown(true);
              }}
              onClick={() => setShowSupplierDropdown(true)}
              id="supplier-dropdown"
            />
          ) : (
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter supplier name"
              required
            />
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Country</Form.Label>
          <Form.Control
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Enter country"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Website</Form.Label>
          <Form.Control
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://..."
          />
        </Form.Group>

        <RepeatableFormSection
          title="Supplier contacts"
          items={contacts}
          getItemKey={(item, index) => item.id ?? `new-${index}`}
          onAdd={addContact}
          onRemove={removeContact}
          loading={contactsLoading}
          isEditMode={isEditMode}
          loadingMessage="Loading contacts..."
          addButtonLabel="+ Add contact"
          addButtonTitle="Add contact"
          addButtonDisabled={isAnySectionLoading}
          removeDisabled={isAnySectionLoading}
        >
          {(item, index) => (
            <>
              <Form.Group className="flex-grow-1" style={{ minWidth: '100px' }}>
                <Form.Control
                  type="text"
                  placeholder="First name"
                  value={item.first_name ?? ''}
                  onChange={(e) => updateContact(index, 'first_name', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="flex-grow-1" style={{ minWidth: '100px' }}>
                <Form.Control
                  type="text"
                  placeholder="Last name"
                  value={item.last_name ?? ''}
                  onChange={(e) => updateContact(index, 'last_name', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="flex-grow-1" style={{ minWidth: '120px' }}>
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={item.email ?? ''}
                  onChange={(e) => updateContact(index, 'email', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="flex-grow-1" style={{ minWidth: '100px' }}>
                <Form.Control
                  type="text"
                  placeholder="Phone"
                  value={item.phone_number ?? ''}
                  onChange={(e) => updateContact(index, 'phone_number', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="flex-grow-1" style={{ minWidth: '100px' }}>
                <Form.Control
                  type="text"
                  placeholder="Job title"
                  value={item.job_title ?? ''}
                  onChange={(e) => updateContact(index, 'job_title', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="d-flex align-items-center" style={{ minWidth: '100px' }}>
                <Form.Check
                  type="checkbox"
                  id={`contact-primary-${index}`}
                  label="Primary"
                  checked={!!item.primary_contact}
                  onChange={(e) => updateContact(index, 'primary_contact', e.target.checked)}
                />
              </Form.Group>
            </>
          )}
        </RepeatableFormSection>

        <FormSubmitRow
          isEditMode={isEditMode}
          submitting={submitting}
          deleting={deleting}
          onDelete={handleDelete}
          submitLabel="Submit changes"
          deleteTitle="Delete supplier"
          disabled={!selectedSupplier?.id || isAnySectionLoading}
          createLabel="Create supplier"
        />
      </Form>
    </>
  );
};

export default SuppliersForm;
